import { FunctionComponent, ReactNode, useState } from "react"
import "./wellIndexPage.module.scss"
import { ApolloQueryResult, useQuery } from "@apollo/client"
import { GET_INDEX_WELLS } from "../../graphql/queries/wells"
import { WellIndexTableDataI } from "../../components/wellIndexTable/tableTypes"
import { formatWellsData } from "../../components/wellIndexTable/tableUtils"
import WellIndexTable from "../../components/wellIndexTable"
import styles from "./wellIndexPage.module.scss"
import { SyncLoader } from "react-spinners"
import EmptyState from "../../components/wellIndexTable/emptyState"
import { useViewerContext } from "../../context/ViewerContext"
import dayjs from "dayjs"
import { getZoneInfoFromEnum } from "../../graphql/enum/zoneInfo"
import {
    Exact,
    GetIndexWellsQuery,
    InputMaybe,
    Scalars,
    TimeIntervalInput,
    WellFilter,
    ZoneInfo,
} from "../../generated/graphql"

// types
export type WellIndexRefetchT = (
    variables?:
        | Partial<
              Exact<{
                  first?: InputMaybe<Scalars["Int"]>
                  windowedRunTimeInterval: TimeIntervalInput
                  zoneInfo?: InputMaybe<ZoneInfo>
                  filter?: InputMaybe<WellFilter>
              }>
          >
        | undefined
) => Promise<ApolloQueryResult<GetIndexWellsQuery>>

// configuration constants
export const WELL_INDEX_FAVORITES_STORAGE_KEY = (subjectId: string): string => {
    return `wellIndexByFavoritesOnly::${subjectId}`
}

// components
const WellIndexPage: FunctionComponent = () => {
    // context
    const {
        identity: { zoneInfo },
        userId,
    } = useViewerContext().getViewer()

    // state
    const [favoritesOnly, setFavoritesOnly] = useState(() => {
        const key = WELL_INDEX_FAVORITES_STORAGE_KEY(userId)
        const storedValue = localStorage.getItem(key)
        return storedValue === "true"
    })

    const {
        error,
        loading,
        data,
        refetch: refetchWellIndex,
    } = useQuery(GET_INDEX_WELLS, {
        variables: {
            first: 1000,
            filter: {
                favorites: favoritesOnly,
            },
            windowedRunTimeInterval: {
                from: {
                    unixMilliseconds: dayjs()
                        .tz(getZoneInfoFromEnum(zoneInfo))
                        .subtract(8, "day")
                        .startOf("day")
                        .valueOf(),
                },
                to: {
                    unixMilliseconds: dayjs()
                        .tz(getZoneInfoFromEnum(zoneInfo))
                        .startOf("day")
                        .valueOf(),
                },
            },
            zoneInfo: zoneInfo,
        },
        // notifyOnNetworkStatusChange: If true, the in-progress query's associated component re-renders whenever the network status changes or a network error occurs.
        notifyOnNetworkStatusChange: true,
        // Because we are doing optimistic updates for well favorites - we cannot set the network policy to network-only.
        // Those two settings together will not broadcast the updates to the query
        // This is a bug in apollo client that they have acknowledged they don't have a good way to fix
        // See here: https://github.com/apollographql/apollo-client/issues/10802
        // Cache and network is the best option for now.
        fetchPolicy: "cache-and-network",
        // onCompleted is called when the query is completed successfully
        // in the event a user has favorites only selected and no favorites are returned we will set favoritesOnly to false which will fire the query again b/c of a parameter change.
        // setFavoritesOnly to false prevents an infinite loop of refetching
        onCompleted: (data) => {
            if (favoritesOnly && formatWellsData(data).length === 0) {
                setFavoritesOnly(false)
                localStorage.setItem(
                    WELL_INDEX_FAVORITES_STORAGE_KEY(userId),
                    "false"
                )
            }
        },
    })

    let body: ReactNode

    if (error) {
        console.error("Error - Well index page:", error)
        body = <Error />
    } else if (loading) {
        body = <Loading />
    } else if (data) {
        const formattedData: WellIndexTableDataI[] = formatWellsData(data)
        if (formattedData.length === 0 && !favoritesOnly) {
            body = <EmptyState />
        } else {
            body = (
                <WellIndexTable
                    refetchWellIndex={refetchWellIndex}
                    data={formattedData}
                />
            )
        }
    }

    return <div className={styles.container}>{body}</div>
}

export default WellIndexPage

const Error: FunctionComponent = () => {
    return (
        <div className={styles.wellIndexError}>
            An error has occurred while loading your wells.
        </div>
    )
}

const Loading: FunctionComponent = () => {
    return (
        <div className={styles.wellIndexLoader}>
            <SyncLoader color="#1e88e5" size={13} />
        </div>
    )
}
